import { useEffect, useRef } from 'react';
import { useAppStore } from '@store/appStore';
import { PlaybackService } from '@dukkan/shared-core/services/navigation';

/**
 * Configuration for usePlayback hook
 */
export interface UsePlaybackConfig {
  /**
   * Optional list of frame indices to cycle through
   * If provided, playback will only navigate through these frames
   * If not provided, playback uses all cameras in the store
   */
  frameList?: number[];
}

/**
 * Hook to handle playback logic
 * Runs the actual playback loop when playback is active
 * Supports both all-cameras playback and filtered frame list playback
 * 
 * @param config Optional configuration for filtered playback
 */
export function usePlayback(config?: UsePlaybackConfig) {
  const playback = useAppStore(state => state.playback);
  const cameras = useAppStore(state => state.cameras);
  const frameList = config?.frameList;

  // Use ref to store the interval ID
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Determine which frames we're cycling through
    const framesToCycle = frameList && frameList.length > 0 ? frameList : null;
    const isFilteredMode = framesToCycle !== null;
    
    // If playback is active, start the playback loop
    if (playback.isPlaying && playback.mode && cameras.length > 0) {
      // Validate that filtered mode has valid frames
      if (isFilteredMode && framesToCycle.length === 0) {
        console.warn(`⚠️ [usePlayback] Filtered mode requested but frameList is empty, stopping playback`);
        useAppStore.getState().stopPlayback();
        return;
      }

      const modeLabel = isFilteredMode ? `filtered ${playback.mode} (${framesToCycle.length} frames)` : playback.mode;
      console.log(`▶️ [usePlayback] Starting ${modeLabel} playback at ${playback.speed}x speed`);

      // Calculate interval based on speed
      const interval = PlaybackService.calculateInterval(playback.speed);

      intervalRef.current = setInterval(() => {
        // Get current state inside the interval callback
        const currentState = useAppStore.getState();
        const currentIndex = currentState.currentCameraIndex;
        const cameras = currentState.cameras;
        
        let nextIndex: number | null = null;
        const playbackFrames = frameList && frameList.length > 0 ? frameList : null;

        if (playbackFrames) {
          // FILTERED MODE: Cycle through provided frame list
          if (playback.mode === 'sequence') {
            // Sequential playback: find current position in frameList and go to next
            const currentPos = playbackFrames.indexOf(currentIndex);
            if (currentPos >= 0 && currentPos < playbackFrames.length - 1) {
              // Current frame is in list, go to next
              nextIndex = playbackFrames[currentPos + 1];
            } else if (currentPos === playbackFrames.length - 1) {
              // At end of filtered list
              nextIndex = null;
            } else {
              // Current frame not in list, start from beginning
              nextIndex = playbackFrames[0];
            }
          } else if (playback.mode === 'random') {
            // Random playback: pick random frame from the list
            if (playbackFrames.length > 1) {
              let randomIdx: number;
              do {
                randomIdx = Math.floor(Math.random() * playbackFrames.length);
              } while (playbackFrames[randomIdx] === currentIndex && playbackFrames.length > 1);
              nextIndex = playbackFrames[randomIdx];
            }
          }
        } else {
          // ALL-CAMERAS MODE: Original playback logic
          if (playback.mode === 'sequence') {
            // Simple sequential playback: just go to next camera in array
            nextIndex = currentIndex + 1;
            if (nextIndex >= cameras.length) {
              nextIndex = null; // End of sequence
            }
          } else if (playback.mode === 'random') {
            // Random playback: pick random camera within reasonable range
            const range = Math.min(50, Math.floor(cameras.length / 4)); // Up to 50 frames or 1/4 of total
            const min = Math.max(0, currentIndex - range);
            const max = Math.min(cameras.length - 1, currentIndex + range);
            
            if (min < max) {
              do {
                nextIndex = Math.floor(Math.random() * (max - min + 1)) + min;
              } while (nextIndex === currentIndex && max > min); // Avoid same frame if possible
            }
          }
        }

        if (nextIndex !== null && nextIndex >= 0 && nextIndex < cameras.length) {
          const modeLabel = playbackFrames ? `filtered ${playback.mode}` : playback.mode;
          console.log(`▶️ [usePlayback] Highlighting thumbnail index ${nextIndex} (${modeLabel})`);
          // Only update thumbnail highlighting - do NOT navigate the viewer
          currentState.setPlayingThumbnailIndex(nextIndex);
        } else {
          // No more frames to play, stop playback
          console.log(`⏹️ [usePlayback] No more frames to play, stopping playback`);
          currentState.stopPlayback();
        }
      }, interval);

      console.log(`▶️ [usePlayback] Playback loop started with ${interval}ms interval (${isFilteredMode ? 'filtered' : 'all-cameras'} mode)`);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [playback.isPlaying, playback.mode, playback.speed, cameras.length, frameList]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
}